/* eslint-disable max-len */
import { first } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRetrieveBlockedAppointmentTimeSlotQuery } from '../../../../api/queries/retrieveBlockedAppointmentTimeSlot';
import { AssetCondition, LocalCustomerFieldSource } from '../../../../api/types';
import { useUploadOcrFiles } from '../../../../components/ocr';
import PortalLoadingElement from '../../../../components/PortalLoadingElement';
import { useThemeComponents } from '../../../../themes';
import useHandleError from '../../../../utilities/useHandleError';
import { hasAppointmentScenario } from '../../../admin/ModuleDetailsPage/modules/implementations/shared';
import useAgreementSubmission from '../../../shared/CIPage/ConsentAndDeclarations/useAgreementSubmission';
import refineCustomerValues from '../../../shared/refineCustomerValues';
import useUploadApplicationDocuments from '../../../shared/useUploadApplicationDocuments';
import { useFirstAvailableSlot } from '../../StandardApplicationEntrypoint/AppointmentPage/useAppointmentAvailability';
import useAppointmentSubmission from '../../StandardApplicationEntrypoint/AppointmentPage/useAppointmentSubmission';
import useCustomerDetailsSubmission from '../../StandardApplicationEntrypoint/CustomerDetailsPage/useCustomerDetailsSubmission';
import useUpdateApplicationFields from '../../StandardApplicationEntrypoint/CustomerDetailsPage/useUpdateApplicationFields';
import ApplicantForm from '../ApplicantForm';
import { type ApplicantFormValues } from '../ApplicantForm/shared';
import useSubmitDraft from '../useSubmitDraft';
import { useEventJourneyKycAndAgreementContext } from './EventJourneyKycAndAgreement';
import { useEventJourneySetupContext } from './EventJourneySetup';

const EventEntrypointInner = () => {
    const { t } = useTranslation(['eventApplicantForm']);
    const { notification } = useThemeComponents();

    const navigate = useNavigate();

    const { event, endpoint, eventModule, customizedFieldsInput } = useEventJourneySetupContext();
    const { customerKind, prefill } = useEventJourneyKycAndAgreementContext();

    const submitDraft = useSubmitDraft(event.id, endpoint);
    const submitAgreements = useAgreementSubmission();
    const submitCustomerDetails = useCustomerDetailsSubmission();
    const updateApplicationFields = useUpdateApplicationFields();
    const uploadOcrFiles = useUploadOcrFiles();
    const submitAppointment = useAppointmentSubmission();

    // Can't use src/app/pages/shared/useUploadApplicationDocument.ts here
    // Because journey was not initialized yet
    // So use src/app/pages/shared/useUploadApplicationDocuments.ts instead
    const uploadApplicationDocuments = useUploadApplicationDocuments();

    const onSubmit = useHandleError(
        async ({ appointment, ...values }: ApplicantFormValues) => {
            notification.loading({
                content: t('eventApplicantForm:messages.creationSubmitting'),
                duration: 0,
                key: 'primary',
            });

            const submitDraftApplicationResult = await submitDraft(values, customerKind);

            const submitAgreementResult = await submitAgreements(
                submitDraftApplicationResult.token,
                values.agreements,
                customerKind,
                values.hasGuarantor
            );

            const { applicantFields, customerAssetArray } = refineCustomerValues(values.customer.fields);

            const showCommentsToBank =
                submitDraftApplicationResult?.application?.__typename === 'EventApplication' &&
                submitDraftApplicationResult.application.bank?.showCommentsField;

            if (showCommentsToBank) {
                // we wait to update remarks first
                await updateApplicationFields(submitAgreementResult.token, values.remarks);
                // then we later call the KYC
                // as calling submit customer will immediately call the next step and will have not the remarks
            }

            const submitCustomerDetailsResult = await submitCustomerDetails(
                submitAgreementResult.token,
                applicantFields,
                customerKind,
                prefill
            );
            await uploadOcrFiles(submitAgreementResult.token);

            await uploadOcrFiles(submitCustomerDetailsResult.token);

            if (customerAssetArray.length > 0) {
                await uploadApplicationDocuments(submitCustomerDetailsResult.token, customerAssetArray);
            }

            // if there is an appointment, submit it
            const finalResult =
                values.configuration.testDrive &&
                eventModule?.appointmentModule &&
                eventModule?.appointmentModule?.__typename === 'AppointmentModule' &&
                eventModule?.displayAppointmentDatepicker
                    ? await submitAppointment(
                          submitCustomerDetailsResult.token,
                          appointment,
                          eventModule.appointmentModule,
                          eventModule.displayAppointmentDatepicker,
                          eventModule.company.timeZone
                      )
                    : submitCustomerDetailsResult;
            notification.destroy('primary');

            // go to the journey
            navigate('../apply', { state: { token: finalResult.token } });
        },
        [
            notification,
            t,
            submitDraft,
            customerKind,
            submitAgreements,
            submitCustomerDetails,
            prefill,
            uploadOcrFiles,
            eventModule.appointmentModule,
            eventModule.displayAppointmentDatepicker,
            eventModule.company.timeZone,
            submitAppointment,
            navigate,
            updateApplicationFields,
            uploadApplicationDocuments,
        ]
    );

    const appointmentModule = useMemo(
        () =>
            eventModule.appointmentModule?.__typename === 'AppointmentModule'
                ? eventModule.appointmentModule
                : undefined,
        [eventModule]
    );

    const { data: bookedListing, loading } = useRetrieveBlockedAppointmentTimeSlotQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            appointmentModuleId: appointmentModule?.id,
        },
        skip: !appointmentModule?.id,
    });
    const bookedAppointmentTimeSlots = useMemo(
        () => bookedListing?.retrieveBlockedAppointmentTimeSlot || [],
        [bookedListing?.retrieveBlockedAppointmentTimeSlot]
    );
    const datePrefill = useFirstAvailableSlot(
        appointmentModule,
        bookedAppointmentTimeSlots,
        eventModule?.company?.timeZone
    );

    const initialValues = useMemo(
        (): ApplicantFormValues => ({
            agreements: {},
            customer: { fields: {} },
            configuration: {
                testDrive: (hasAppointmentScenario(event.scenarios) && event.scenarios.length === 1) || false,
                tradeIn: false,
                assetCondition: AssetCondition.New,
            },
            vehicleInterest: {
                model: null,
                subModel: null,
                variant: null,
            },
            isCorporateCustomer: false,
            hasGuarantor: false,
            tradeInVehicle: [
                {
                    isSelected: true,
                    make: null,
                    model: null,
                    registrationNumber: null,
                    source: LocalCustomerFieldSource.UserInput,
                },
            ],
            dealerId: first(event.dealerIds),
            prefill: false,
            customizedFields: customizedFieldsInput,
            appointment: {
                date: datePrefill?.date || null,
                time: datePrefill?.firstSlot?.value || null,
                useCurrentDateTime: false,
            },
            remarks: '',
        }),
        [customizedFieldsInput, datePrefill?.date, datePrefill?.firstSlot?.value, event.dealerIds, event.scenarios]
    );

    if (loading) {
        return <PortalLoadingElement />;
    }

    return <ApplicantForm initialValues={initialValues} onSubmit={onSubmit} />;
};

export default EventEntrypointInner;
